import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, Button, Skeleton, StatusBadge } from '@lineageatlas/design-system';
import { useApi, apiFetch } from '../hooks/useApi';
import type { CellLine, QCEntryType, QCResult } from '@lineageatlas/shared';
import { UC_MSC_POSITIVE_MARKERS, UC_MSC_NEGATIVE_MARKERS } from '@lineageatlas/shared';
import { MarkerRadarChart, type MarkerData } from '../components/charts/MarkerRadarChart';
import { AIAnalysisCard, type AIAnalysis } from '../components/AIAnalysisCard';

// ---------------------------------------------------------------------------
// Marker reference data
// ---------------------------------------------------------------------------

interface MarkerRef {
  name: string;
  expectedExpression: 'positive' | 'negative';
  expectedMin: number;
  expectedMax: number;
  unit: string;
  referenceSource: string;
}

const UC_MSC_MARKER_REFS: MarkerRef[] = [
  ...UC_MSC_POSITIVE_MARKERS.map((m) => ({
    name: m,
    expectedExpression: 'positive' as const,
    expectedMin: 95,
    expectedMax: 100,
    unit: '%',
    referenceSource: 'ISCT 2006',
  })),
  ...UC_MSC_NEGATIVE_MARKERS.map((m) => ({
    name: m,
    expectedExpression: 'negative' as const,
    expectedMin: 0,
    expectedMax: 5,
    unit: '%',
    referenceSource: 'ISCT 2006',
  })),
];

function getMarkersForCellType(cellType: string): MarkerRef[] {
  if (cellType === 'UC-MSC') return UC_MSC_MARKER_REFS;
  return [];
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

type ValidationState = 'pass' | 'warning' | 'fail' | 'empty';

function validateMarkerValue(
  value: string,
  ref: Pick<MarkerRef, 'expectedMin' | 'expectedMax'>,
): ValidationState {
  if (!value) return 'empty';
  const n = parseFloat(value);
  if (isNaN(n)) return 'empty';
  const { expectedMin, expectedMax } = ref;
  const range = expectedMax - expectedMin;
  const buffer = range * 0.1 + 1;
  if (n >= expectedMin && n <= expectedMax) return 'pass';
  if (n >= expectedMin - buffer && n <= expectedMax + buffer) return 'warning';
  return 'fail';
}

function computeOverallResult(states: ValidationState[]): QCResult {
  const filled = states.filter((s) => s !== 'empty');
  if (filled.length === 0) return 'pending';
  if (filled.some((s) => s === 'fail')) return 'fail';
  if (filled.some((s) => s === 'warning')) return 'warning';
  return 'pass';
}

// ---------------------------------------------------------------------------
// Entry type definitions
// ---------------------------------------------------------------------------

const ENTRY_TYPES: Array<{
  value: QCEntryType;
  label: string;
  icon: string;
  description: string;
}> = [
  {
    value: 'flow_cytometry',
    label: 'Flow Cytometry',
    icon: '⬡',
    description: 'Surface marker expression profiling (CD73, CD90, CD105…)',
  },
  {
    value: 'viability',
    label: 'Viability',
    icon: '◉',
    description: 'Cell viability via trypan blue or live/dead staining',
  },
  {
    value: 'morphology',
    label: 'Morphology',
    icon: '⬢',
    description: 'Visual inspection of cell shape and growth pattern',
  },
  {
    value: 'sterility',
    label: 'Sterility',
    icon: '✦',
    description: 'Microbial contamination testing',
  },
  {
    value: 'mycoplasma',
    label: 'Mycoplasma',
    icon: '⊗',
    description: 'PCR-based mycoplasma detection',
  },
  {
    value: 'endotoxin',
    label: 'Endotoxin',
    icon: '⊘',
    description: 'LAL assay for endotoxin quantification',
  },
  {
    value: 'karyotype',
    label: 'Karyotype',
    icon: '⊕',
    description: 'Chromosomal stability assessment',
  },
  { value: 'custom', label: 'Custom', icon: '◈', description: 'Other QC test type' },
];

// ---------------------------------------------------------------------------
// Step indicator
// ---------------------------------------------------------------------------

const STEPS = ['Select Lot', 'Entry Type', 'Enter Results', 'Review & Submit'];

function StepIndicator({ current }: { current: number }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        marginBottom: 'var(--space-8)',
        overflowX: 'auto',
        paddingBottom: 'var(--space-2)',
      }}
      aria-label="Form progress"
    >
      {STEPS.map((step, idx) => {
        const state =
          idx < current ? 'completed' : idx === current ? 'active' : 'pending';
        return (
          <div
            key={step}
            style={{
              display: 'flex',
              alignItems: 'center',
              flex: idx < STEPS.length - 1 ? 1 : undefined,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 'var(--space-1)',
                minWidth: '72px',
              }}
            >
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'var(--text-caption)',
                  fontWeight: 700,
                  background:
                    state === 'completed'
                      ? '#00d4aa'
                      : state === 'active'
                        ? 'var(--color-accent-primary)'
                        : 'rgba(255,255,255,0.06)',
                  color:
                    state === 'completed' || state === 'active'
                      ? '#000'
                      : 'var(--color-text-muted)',
                  border:
                    state === 'active'
                      ? '2px solid var(--color-accent-primary)'
                      : '2px solid transparent',
                  transition: 'all 200ms ease',
                }}
                aria-current={state === 'active' ? 'step' : undefined}
              >
                {state === 'completed' ? '✓' : idx + 1}
              </div>
              <span
                style={{
                  fontSize: '10px',
                  color:
                    state === 'active' ? 'var(--color-accent-primary)' : 'var(--color-text-muted)',
                  fontWeight: state === 'active' ? 600 : 400,
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                }}
              >
                {step}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div
                aria-hidden="true"
                style={{
                  flex: 1,
                  height: '2px',
                  background: idx < current ? '#00d4aa' : 'rgba(255,255,255,0.08)',
                  marginBottom: '18px',
                  transition: 'background 200ms ease',
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live status banner
// ---------------------------------------------------------------------------

function LiveStatusBanner({
  result,
  totalMarkers,
  filledCount,
  warningCount,
  failCount,
}: {
  result: QCResult;
  totalMarkers: number;
  filledCount: number;
  warningCount: number;
  failCount: number;
}) {
  if (filledCount === 0) return null;

  const { bg, border, label, icon } =
    result === 'pass'
      ? {
          bg: 'rgba(0,212,170,0.08)',
          border: 'rgba(0,212,170,0.3)',
          label: `PASSING — ${filledCount}/${totalMarkers} markers in range`,
          icon: '✓',
        }
      : result === 'warning'
        ? {
            bg: 'rgba(251,191,36,0.08)',
            border: 'rgba(251,191,36,0.3)',
            label: `WARNING — ${warningCount} marker${warningCount !== 1 ? 's' : ''} borderline`,
            icon: '⚠',
          }
        : {
            bg: 'rgba(255,107,107,0.08)',
            border: 'rgba(255,107,107,0.3)',
            label: `FAIL — ${failCount} marker${failCount !== 1 ? 's' : ''} out of range`,
            icon: '✕',
          };

  const color = result === 'pass' ? '#00d4aa' : result === 'warning' ? '#fbbf24' : '#ff6b6b';

  return (
    <div
      style={{
        padding: 'var(--space-3) var(--space-4)',
        borderRadius: 'var(--radius-md)',
        background: bg,
        border: `1px solid ${border}`,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        marginBottom: 'var(--space-4)',
        transition: 'all 200ms ease',
      }}
      role="status"
      aria-live="polite"
    >
      <span style={{ fontSize: '18px', color }} aria-hidden="true">
        {icon}
      </span>
      <span style={{ fontSize: 'var(--text-body)', fontWeight: 700, color }}>
        {label}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Marker input card
// ---------------------------------------------------------------------------

interface MarkerInputCardProps {
  ref_: MarkerRef;
  value: string;
  onChange: (v: string) => void;
}

function MarkerInputCard({ ref_, value, onChange }: MarkerInputCardProps) {
  const state = validateMarkerValue(value, ref_);
  const borderColor =
    state === 'pass'
      ? '#00d4aa'
      : state === 'warning'
        ? '#fbbf24'
        : state === 'fail'
          ? '#ff6b6b'
          : 'var(--color-border)';
  const bg =
    state === 'pass'
      ? 'rgba(0,212,170,0.06)'
      : state === 'warning'
        ? 'rgba(251,191,36,0.06)'
        : state === 'fail'
          ? 'rgba(255,107,107,0.06)'
          : 'rgba(255,255,255,0.02)';
  const stateIcon =
    state === 'pass' ? '✓' : state === 'warning' ? '⚠' : state === 'fail' ? '✕' : '';
  const stateColor =
    state === 'pass'
      ? '#00d4aa'
      : state === 'warning'
        ? '#fbbf24'
        : state === 'fail'
          ? '#ff6b6b'
          : 'transparent';

  return (
    <div
      style={{
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-md)',
        background: bg,
        border: `1.5px solid ${borderColor}`,
        transition: 'border-color 150ms ease, background 150ms ease',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <label
            htmlFor={`marker-${ref_.name}`}
            style={{
              fontSize: 'var(--text-body)',
              fontWeight: 700,
              color: 'var(--color-text)',
              fontFamily: 'var(--font-mono)',
              display: 'block',
            }}
          >
            {ref_.name}
          </label>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            Expected:{' '}
            {ref_.expectedExpression === 'positive'
              ? `≥${ref_.expectedMin}%`
              : `≤${ref_.expectedMax}%`}
          </span>
        </div>
        {state !== 'empty' && (
          <span style={{ fontSize: '18px', color: stateColor }} aria-hidden="true">
            {stateIcon}
          </span>
        )}
      </div>

      {/* Large centered input */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <input
          id={`marker-${ref_.name}`}
          type="number"
          min={0}
          max={100}
          step={0.1}
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="—"
          aria-label={`${ref_.name} percentage value`}
          style={{
            flex: 1,
            height: '52px',
            padding: '0 var(--space-3)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border)',
            background: 'var(--color-bg-input, #0f0f11)',
            color: 'var(--color-text)',
            fontSize: '24px',
            fontWeight: 700,
            fontFamily: 'var(--font-mono)',
            fontVariantNumeric: 'tabular-nums',
            textAlign: 'center',
            outline: 'none',
            transition: 'border-color 150ms ease',
          }}
        />
        <span
          style={{
            fontSize: 'var(--text-body)',
            color: 'var(--color-text-muted)',
            minWidth: '20px',
          }}
        >
          {ref_.unit}
        </span>
      </div>

      {/* Validation message */}
      {state !== 'empty' && (
        <div
          style={{
            fontSize: 'var(--text-xs)',
            fontWeight: 600,
            color: stateColor,
          }}
        >
          {state === 'pass'
            ? 'In range'
            : state === 'warning'
              ? 'Borderline — check again'
              : 'Out of range'}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Success state
// ---------------------------------------------------------------------------

function SuccessState({
  onViewLot,
  onAddAnother,
  aiAnalysis,
  aiLoading,
}: {
  onViewLot: () => void;
  onAddAnother: () => void;
  aiAnalysis: AIAnalysis | null;
  aiLoading: boolean;
}) {
  return (
    <div className="page-enter">
      <div
        style={{
          textAlign: 'center',
          padding: 'var(--space-8) 0 var(--space-6)',
        }}
      >
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: 'rgba(0,212,170,0.12)',
            border: '2px solid rgba(0,212,170,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '28px',
            color: '#00d4aa',
            margin: '0 auto var(--space-4)',
            animation: 'pulse-soft 2s ease-in-out',
          }}
          aria-hidden="true"
        >
          ✓
        </div>
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-h2)',
            fontWeight: 700,
            color: '#00d4aa',
            margin: '0 0 var(--space-2)',
          }}
        >
          QC Entry Submitted
        </h2>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-body)', margin: 0 }}>
          The QC entry has been recorded successfully.
        </p>
      </div>

      {/* AI Analysis */}
      {aiLoading && (
        <Card style={{ marginBottom: 'var(--space-6)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <div
              style={{
                width: '20px',
                height: '20px',
                border: '2px solid var(--color-accent-primary)',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
              aria-hidden="true"
            />
            <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-body)' }}>
              AI analysis in progress…
            </span>
          </div>
        </Card>
      )}
      {aiAnalysis && <AIAnalysisCard analysis={aiAnalysis} />}

      <div
        style={{
          display: 'flex',
          gap: 'var(--space-3)',
          justifyContent: 'center',
          marginTop: 'var(--space-6)',
        }}
      >
        <Button variant="primary" onClick={onViewLot}>
          View Lot
        </Button>
        <Button variant="secondary" onClick={onAddAnother}>
          Add Another
        </Button>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Responsive styles
// ---------------------------------------------------------------------------

const qcStyles = `
  @media (min-width: 1024px) {
    .qc-markers-layout { grid-template-columns: 1fr 340px !important; }
  }
  @media (min-width: 640px) {
    .qc-markers-grid { grid-template-columns: repeat(2, 1fr) !important; }
  }
  @media (prefers-reduced-motion: reduce) {
    .page-enter { animation: none !important; }
  }
`;

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function QCEntryPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const preselectedLotId = searchParams.get('lotId') ?? '';

  // Step state
  const [step, setStep] = useState(0);

  // Step 1: lot
  const [selectedLotId, setSelectedLotId] = useState(preselectedLotId);

  // Step 2: type + passage
  const [entryType, setEntryType] = useState<QCEntryType | ''>('');
  const [passage, setPassage] = useState('');

  // Step 3: marker values
  const [markerValues, setMarkerValues] = useState<Record<string, string>>({});

  // Step 4: notes
  const [notes, setNotes] = useState('');

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [createdEntryId, setCreatedEntryId] = useState<string | null>(null);
  const [aiAnalysis, setAiAnalysis] = useState<AIAnalysis | null>(null);
  const [aiLoading, setAiLoading] = useState(false);

  const { data: lots, loading: lotsLoading } = useApi<CellLine[]>('/api/v1/lots');
  const { data: selectedLot } = useApi<CellLine>(
    selectedLotId ? `/api/v1/lots/${selectedLotId}` : '',
  );

  const markers = useMemo(
    () => (selectedLot ? getMarkersForCellType(selectedLot.cell_type) : []),
    [selectedLot],
  );

  const validationStates = useMemo<ValidationState[]>(
    () =>
      markers.map((m) =>
        validateMarkerValue(markerValues[m.name] ?? '', {
          expectedMin: m.expectedMin,
          expectedMax: m.expectedMax,
        }),
      ),
    [markers, markerValues],
  );

  const overallResult = useMemo(
    () => computeOverallResult(validationStates),
    [validationStates],
  );

  const filledCount = validationStates.filter((s) => s !== 'empty').length;
  const warningCount = validationStates.filter((s) => s === 'warning').length;
  const failCount = validationStates.filter((s) => s === 'fail').length;

  // Live radar markers
  const radarMarkers: MarkerData[] = useMemo(
    () =>
      markers
        .filter((m) => markerValues[m.name] !== undefined && markerValues[m.name] !== '')
        .map((m) => ({
          name: m.name,
          value: parseFloat(markerValues[m.name] ?? '0') || 0,
          min: m.expectedMin,
          max: m.expectedMax,
          result: validateMarkerValue(markerValues[m.name] ?? '', m) === 'pass' ? 'pass' : validateMarkerValue(markerValues[m.name] ?? '', m) === 'warning' ? 'warning' : 'fail',
        })),
    [markers, markerValues],
  );

  // Poll AI analysis after submission
  useEffect(() => {
    if (!createdEntryId) return;
    setAiLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await apiFetch<{ ai_analysis?: string | null }>(
          `/api/v1/qc-entries/${createdEntryId}`,
        );
        if (res.data.ai_analysis) {
          setAiAnalysis(JSON.parse(res.data.ai_analysis) as AIAnalysis);
        }
      } catch {
        // silently ignore
      } finally {
        setAiLoading(false);
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [createdEntryId]);

  const handleSubmit = useCallback(async () => {
    if (!selectedLotId || !entryType) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const markerPayload = markers.map((m) => ({
        marker_name: m.name,
        value: parseFloat(markerValues[m.name] ?? '0') || 0,
        unit: m.unit,
        expected_min: m.expectedMin,
        expected_max: m.expectedMax,
        reference_source: m.referenceSource,
        result:
          validateMarkerValue(markerValues[m.name] ?? '', m) === 'pass'
            ? 'in_range'
            : validateMarkerValue(markerValues[m.name] ?? '', m) === 'warning'
              ? 'borderline'
              : 'out_of_range',
      }));

      const res = await apiFetch<{ id: string }>('/api/v1/qc-entries', {
        method: 'POST',
        body: JSON.stringify({
          cell_line_id: selectedLotId,
          entry_type: entryType,
          passage_number: passage ? parseInt(passage) : null,
          overall_result: overallResult,
          notes: notes.trim() || null,
          markers: markers.length > 0 ? markerPayload : undefined,
        }),
      });
      setCreatedEntryId(res.data.id);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit QC entry.');
    } finally {
      setSubmitting(false);
    }
  }, [selectedLotId, entryType, passage, markers, markerValues, overallResult, notes]);

  // ── Success state ──────────────────────────────────────────────────────────

  if (submitted) {
    return (
      <>
        <style>{qcStyles}</style>
        <div style={{ maxWidth: '640px', margin: '0 auto' }}>
          <SuccessState
            onViewLot={() => navigate(`/lots/${selectedLotId}`)}
            onAddAnother={() => {
              setSubmitted(false);
              setStep(0);
              setSelectedLotId('');
              setEntryType('');
              setPassage('');
              setMarkerValues({});
              setNotes('');
              setCreatedEntryId(null);
              setAiAnalysis(null);
            }}
            aiAnalysis={aiAnalysis}
            aiLoading={aiLoading}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <style>{qcStyles}</style>
      <div className="page-enter">
        {/* Page header */}
        <div style={{ marginBottom: 'var(--space-6)' }}>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-h1)',
              fontWeight: 700,
              letterSpacing: 'var(--tracking-heading)',
              color: 'var(--color-text)',
              margin: '0 0 var(--space-2)',
            }}
          >
            New QC Entry
          </h1>
          <p
            style={{
              color: 'var(--color-text-muted)',
              fontSize: 'var(--text-body)',
              margin: 0,
            }}
          >
            Record quality control test results for a cell product lot.
          </p>
        </div>

        {/* Step indicator */}
        <StepIndicator current={step} />

        {/* ── Step 0: Select Lot ── */}
        {step === 0 && (
          <Card padding="spacious" style={{ maxWidth: '560px' }}>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--text-h3)',
                fontWeight: 700,
                color: 'var(--color-text)',
                margin: '0 0 var(--space-6)',
              }}
            >
              Select Lot
            </h2>

            {lotsLoading ? (
              <Skeleton height="44px" />
            ) : (
              <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <span
                  style={{
                    fontSize: 'var(--text-xs)',
                    fontWeight: 600,
                    color: 'var(--color-text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Cell Line / Lot
                </span>
                <select
                  value={selectedLotId}
                  onChange={(e) => setSelectedLotId(e.target.value)}
                  aria-label="Select a lot"
                  style={{
                    height: '44px',
                    padding: '0 var(--space-4)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-bg-input, #0f0f11)',
                    color: 'var(--color-text)',
                    fontSize: 'var(--text-body)',
                    fontFamily: 'var(--font-body)',
                    outline: 'none',
                    appearance: 'none',
                    backgroundImage:
                      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23888' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")",
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right var(--space-3) center',
                    paddingRight: 'var(--space-8)',
                  }}
                >
                  <option value="">— Choose a lot —</option>
                  {(lots ?? []).map((lot) => (
                    <option key={lot.id} value={lot.id}>
                      {lot.name} ({lot.status})
                    </option>
                  ))}
                </select>
              </label>
            )}

            {/* Lot info card after selection */}
            {selectedLot && (
              <div
                style={{
                  marginTop: 'var(--space-4)',
                  padding: 'var(--space-4)',
                  background: 'rgba(255,255,255,0.03)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--glass-border-color)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-2)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <span
                    style={{
                      fontWeight: 700,
                      fontFamily: 'var(--font-mono)',
                      color: 'var(--color-text)',
                    }}
                  >
                    {selectedLot.name}
                  </span>
                  <StatusBadge variant="lot" lotStatus={selectedLot.status} size="sm" />
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 'var(--space-4)',
                    fontSize: 'var(--text-caption)',
                    color: 'var(--color-text-muted)',
                    flexWrap: 'wrap',
                  }}
                >
                  <span>P{selectedLot.current_passage}/{selectedLot.max_passage}</span>
                  <span>{selectedLot.vials_remaining ?? '—'} vials</span>
                  <span>{selectedLot.storage_location ?? '—'}</span>
                </div>
              </div>
            )}

            <div style={{ marginTop: 'var(--space-6)', display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="primary"
                onClick={() => setStep(1)}
                disabled={!selectedLotId}
              >
                Continue →
              </Button>
            </div>
          </Card>
        )}

        {/* ── Step 1: Entry type ── */}
        {step === 1 && (
          <div style={{ maxWidth: '720px' }}>
            <Card padding="spacious" style={{ marginBottom: 'var(--space-4)' }}>
              <h2
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--text-h3)',
                  fontWeight: 700,
                  color: 'var(--color-text)',
                  margin: '0 0 var(--space-6)',
                }}
              >
                Select Test Type
              </h2>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: 'var(--space-3)',
                  marginBottom: 'var(--space-6)',
                }}
              >
                {ENTRY_TYPES.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setEntryType(type.value)}
                    aria-pressed={entryType === type.value}
                    style={{
                      padding: 'var(--space-4)',
                      borderRadius: 'var(--radius-md)',
                      border: `2px solid ${entryType === type.value ? 'var(--color-accent-primary)' : 'var(--glass-border-color)'}`,
                      background:
                        entryType === type.value
                          ? 'rgba(0,212,170,0.08)'
                          : 'rgba(255,255,255,0.02)',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 150ms ease',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'var(--space-2)',
                      color: 'inherit',
                    }}
                  >
                    <span
                      style={{ fontSize: '20px', color: entryType === type.value ? 'var(--color-accent-primary)' : 'var(--color-text-muted)' }}
                      aria-hidden="true"
                    >
                      {type.icon}
                    </span>
                    <span
                      style={{
                        fontWeight: 700,
                        fontSize: 'var(--text-body)',
                        color: 'var(--color-text)',
                      }}
                    >
                      {type.label}
                    </span>
                    <span
                      style={{
                        fontSize: 'var(--text-xs)',
                        color: 'var(--color-text-muted)',
                        lineHeight: 1.4,
                      }}
                    >
                      {type.description}
                    </span>
                  </button>
                ))}
              </div>

              {/* Passage input */}
              <label
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-2)',
                  maxWidth: '200px',
                }}
              >
                <span
                  style={{
                    fontSize: 'var(--text-xs)',
                    fontWeight: 600,
                    color: 'var(--color-text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Passage Number
                </span>
                <input
                  type="number"
                  min={0}
                  value={passage}
                  onChange={(e) => setPassage(e.target.value)}
                  placeholder="e.g. 5"
                  aria-label="Passage number"
                  style={{
                    height: '44px',
                    padding: '0 var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-bg-input, #0f0f11)',
                    color: 'var(--color-text)',
                    fontSize: 'var(--text-body)',
                    fontFamily: 'var(--font-mono)',
                    outline: 'none',
                  }}
                />
              </label>

              <div
                style={{
                  marginTop: 'var(--space-6)',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Button variant="quiet" onClick={() => setStep(0)}>
                  ← Back
                </Button>
                <Button
                  variant="primary"
                  onClick={() => setStep(2)}
                  disabled={!entryType}
                >
                  Continue →
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* ── Step 2: Enter Results ── */}
        {step === 2 && (
          <div>
            {/* Live status banner */}
            <LiveStatusBanner
              result={overallResult}
              totalMarkers={markers.length}
              filledCount={filledCount}
              warningCount={warningCount}
              failCount={failCount}
            />

            {markers.length > 0 ? (
              <div
                className="qc-markers-layout"
                style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 'var(--space-6)' }}
              >
                {/* Marker grid */}
                <div>
                  <h3
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: 'var(--text-h4)',
                      fontWeight: 700,
                      color: 'var(--color-text)',
                      margin: '0 0 var(--space-4)',
                    }}
                  >
                    Marker Values
                  </h3>
                  <div
                    className="qc-markers-grid"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr',
                      gap: 'var(--space-3)',
                    }}
                  >
                    {markers.map((m) => (
                      <MarkerInputCard
                        key={m.name}
                        ref_={m}
                        value={markerValues[m.name] ?? ''}
                        onChange={(v) =>
                          setMarkerValues((prev) => ({ ...prev, [m.name]: v }))
                        }
                      />
                    ))}
                  </div>
                </div>

                {/* Live radar chart */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-4)',
                    position: 'sticky',
                    top: '80px',
                  }}
                >
                  <Card>
                    <h3
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: 'var(--text-h4)',
                        fontWeight: 700,
                        color: 'var(--color-text)',
                        margin: '0 0 var(--space-4)',
                      }}
                    >
                      Live Profile
                    </h3>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <MarkerRadarChart
                        markers={
                          radarMarkers.length >= 3
                            ? radarMarkers
                            : markers.map((m) => ({
                                name: m.name,
                                value: 0,
                                min: m.expectedMin,
                                max: m.expectedMax,
                                result: 'pending',
                              }))
                        }
                        mode="full"
                      />
                    </div>
                  </Card>
                </div>
              </div>
            ) : (
              <Card padding="spacious">
                <p
                  style={{
                    color: 'var(--color-text-muted)',
                    fontSize: 'var(--text-body)',
                    textAlign: 'center',
                    padding: 'var(--space-6) 0',
                  }}
                >
                  No predefined markers for this test type. Results will be recorded as-is.
                </p>
              </Card>
            )}

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 'var(--space-6)',
              }}
            >
              <Button variant="quiet" onClick={() => setStep(1)}>
                ← Back
              </Button>
              <Button variant="primary" onClick={() => setStep(3)}>
                Review →
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 3: Review ── */}
        {step === 3 && (
          <div style={{ maxWidth: '640px' }}>
            <Card padding="spacious" style={{ marginBottom: 'var(--space-4)' }}>
              <h2
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--text-h3)',
                  fontWeight: 700,
                  color: 'var(--color-text)',
                  margin: '0 0 var(--space-6)',
                }}
              >
                Review &amp; Submit
              </h2>

              {/* Summary */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-3)',
                  marginBottom: 'var(--space-6)',
                }}
              >
                {[
                  { label: 'Lot', value: selectedLot?.name ?? selectedLotId },
                  {
                    label: 'Test Type',
                    value:
                      ENTRY_TYPES.find((t) => t.value === entryType)?.label ?? entryType,
                  },
                  { label: 'Passage', value: passage ? `P${passage}` : '—' },
                  {
                    label: 'Overall Result',
                    value: (
                      <StatusBadge variant="qc" qcResult={overallResult} size="md" />
                    ),
                  },
                  {
                    label: 'Markers Entered',
                    value: `${filledCount} / ${markers.length}`,
                  },
                ].map(({ label, value }) => (
                  <div
                    key={label}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: 'var(--space-3) 0',
                      borderBottom: '1px solid var(--glass-border-color)',
                      fontSize: 'var(--text-body)',
                    }}
                  >
                    <span style={{ color: 'var(--color-text-muted)' }}>{label}</span>
                    <span
                      style={{ color: 'var(--color-text)', fontWeight: 600 }}
                    >
                      {value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Marker summary */}
              {markers.length > 0 && filledCount > 0 && (
                <div style={{ marginBottom: 'var(--space-6)' }}>
                  <div
                    style={{
                      fontSize: 'var(--text-xs)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: 'var(--color-text-muted)',
                      marginBottom: 'var(--space-3)',
                    }}
                  >
                    Marker Summary
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                      gap: 'var(--space-2)',
                    }}
                  >
                    {markers.map((m, idx) => {
                      const state = validationStates[idx];
                      const val = markerValues[m.name];
                      if (!val || state === 'empty') return null;
                      const color =
                        state === 'pass'
                          ? '#00d4aa'
                          : state === 'warning'
                            ? '#fbbf24'
                            : '#ff6b6b';
                      return (
                        <div
                          key={m.name}
                          style={{
                            padding: 'var(--space-2) var(--space-3)',
                            borderRadius: 'var(--radius-md)',
                            background: 'rgba(255,255,255,0.03)',
                            border: `1px solid ${color}30`,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: 'var(--text-caption)',
                          }}
                        >
                          <span
                            style={{
                              fontFamily: 'var(--font-mono)',
                              color: 'var(--color-text)',
                              fontWeight: 600,
                            }}
                          >
                            {m.name}
                          </span>
                          <span
                            style={{
                              color,
                              fontFamily: 'var(--font-mono)',
                              fontWeight: 700,
                            }}
                          >
                            {parseFloat(val).toFixed(1)}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div style={{ marginBottom: 'var(--space-6)' }}>
                <label
                  htmlFor="qc-notes"
                  style={{
                    display: 'block',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 600,
                    color: 'var(--color-text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: 'var(--space-2)',
                  }}
                >
                  Notes (optional)
                </label>
                <textarea
                  id="qc-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any observations or comments…"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-bg-input, #0f0f11)',
                    color: 'var(--color-text)',
                    fontSize: 'var(--text-body)',
                    fontFamily: 'var(--font-body)',
                    outline: 'none',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    lineHeight: 1.5,
                  }}
                />
              </div>

              {submitError && (
                <div
                  role="alert"
                  style={{
                    padding: 'var(--space-3)',
                    background: 'rgba(255,107,107,0.08)',
                    border: '1px solid rgba(255,107,107,0.3)',
                    borderRadius: 'var(--radius-md)',
                    color: '#ff6b6b',
                    fontSize: 'var(--text-caption)',
                    marginBottom: 'var(--space-4)',
                  }}
                >
                  {submitError}
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Button variant="quiet" onClick={() => setStep(2)} disabled={submitting}>
                  ← Back
                </Button>
                <Button
                  variant="primary"
                  onClick={() => void handleSubmit()}
                  disabled={submitting}
                >
                  {submitting ? 'Submitting…' : 'Submit QC Entry'}
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}
